/**
 * LeagueSubNav — sub-navigation strip at the top of every league page.
 * ─────────────────────────────────────────────────────────────────
 * Renders a horizontal pill-row of links to the sections of a single
 * league (Dashboard / Members / Settings / Venues / etc.). Pages
 * are filtered by the current user's permissions so a viewer or a
 * narrowly-permissioned administrator sees only the sections they
 * can access — this matches Phase 2 acceptance criterion #3.
 *
 * Backend mirror: every API route behind these links is also
 * permission-gated by middleware/leaguePermissions.js. The UI
 * filter here is purely a UX nicety; a hostile user typing the URL
 * directly still gets a 403.
 *
 * SECTIONS DISPLAYED IN PHASE 2a
 *   • Dashboard         — any member
 *   • Members           — any member (write actions gated inside the page)
 *   • Settings          — manager OR administrator with manage_league_settings
 *
 * SECTIONS COMING LATER (currently NOT rendered — comments preserved
 * so future phases drop them in by uncommenting):
 *   • Venues            — Phase 2b (manage_venues)
 *   • Divisions         — Phase 2b (manage_divisions)
 *   • Seasons           — Phase 2c (manage_league_settings)
 *   • Schedule          — Phase 4  (manage_schedule)
 *   • Officials         — Phase 4  (manage_officials)
 *   • Public Page       — Phase 7  (manage_public_page)
 *   • Reports           — Phase 6  (view_reports)
 *
 * The `can(...)` helper from useLeagueMembership exactly mirrors
 * the backend's requireLeaguePermission semantics — manager bypasses,
 * viewer always false, admin checked against permissions_json.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Trophy, FlaskConical } from 'lucide-react';
// v2.5.131 — drop the duplicate icon-set import (LayoutDashboard,
// Users, etc.) since the tab strip is gone. Sidebar handles those
// now. Keeping Trophy for the LEAGUE label icon in the header.
import api from '../../services/api.js';
import { useLeagueMembership } from '../../hooks/useLeagueMembership.js';
import { useAuth } from '../../context/AuthContext.jsx';

export default function LeagueSubNav({ league: leagueProp }) {
  const { leagueId } = useParams();
  // v2.5.131 — isMember / can / isManager were used to filter the
  // tab strip's `visible` flag. Tab strip is now gone, so these
  // gates aren't needed here either. The hook still runs on each
  // page's main component for routing decisions. Keep the import
  // available in case a future variant needs role-based affordances
  // in the header itself.
  useLeagueMembership(leagueId);

  // v2.5.131 — if the caller didn't pass a league (LeagueAnnouncementsPage
  // does this — see the comment in that file), fetch it ourselves so
  // the header still shows the league name + logo. Caching is handled
  // by the api interceptor / browser HTTP cache. This is a small extra
  // request only on the pages that don't already load the league;
  // pages that DO pass a non-null prop skip the fetch entirely.
  const [fetchedLeague, setFetchedLeague] = useState(null);
  useEffect(() => {
    if (leagueProp) return; // caller supplied; nothing to do
    if (!leagueId) return;
    let cancelled = false;
    api.get(`/leagues/${leagueId}`)
      .then(({ data }) => { if (!cancelled) setFetchedLeague(data?.league || null); })
      .catch((err) => {
        // 2026-09-28 — coaches of enrolled teams aren't league members
        // (403 here) but do reach the coaches' channel and game sheets;
        // the chat context names the league for them.
        if (err?.response?.status !== 403) return;
        api.get(`/leagues/${leagueId}/chat/context`)
          .then(({ data }) => { if (!cancelled && data?.league) setFetchedLeague(data.league); })
          .catch(() => { /* leave name as "Loading…" */ });
      });
    return () => { cancelled = true; };
  }, [leagueProp, leagueId]);
  const league = leagueProp || fetchedLeague;
  const { user } = useAuth();
  // 2026-09-28 — demo leagues (Admin → Demo) carry a banner on every
  // league page so nobody mistakes the fictional data for a real league.
  // Pages that pass their own league object get is_demo from
  // GET /leagues/:id; a prop without the field falls back to our fetch.
  const [demoFlag, setDemoFlag] = useState(null);
  useEffect(() => {
    if (!leagueId) return;
    if (league && typeof league.is_demo === 'boolean') { setDemoFlag(league.is_demo); return; }
    if (!leagueProp) return;   // the fetch above will fill `league`
    let cancelled = false;
    api.get(`/leagues/${leagueId}`)
      .then(({ data }) => { if (!cancelled) setDemoFlag(data?.league?.is_demo === true); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [leagueId, league, leagueProp]);
  const isDemo = demoFlag === true || league?.is_demo === true;

  // v2.5.131 — items array removed (was used to build the tab strip).

  return (
    <div style={{ marginBottom: 24 }}>
      {/* League name header — shows up on every league page so the
          user always knows which league they're inside. v2.5.124
          adds the LEAGUE MODE pill on the right so the user can
          tell at a glance they're in league mode (vs team mode)
          and tap to switch back. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'var(--ice-a10)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          overflow: 'hidden',
        }}>
          {/* v2.5.131 — render the league's uploaded logo here if
              one is set; fall back to the trophy icon otherwise.
              objectFit:contain so non-square logos display fully
              without cropping. */}
          {league?.logo_url ? (
            <img
              src={league.logo_url}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Trophy size={18} color="var(--ice)" />
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 1 }}>
            League
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
            {league?.name || 'Loading…'}
          </div>
        </div>

        {/* Mode switcher pill — v2.5.124. Visual indicator + click
            target. Hover state previews the "→ Team" affordance. */}
        <Link
          to="/dashboard"
          title="Switch to Team mode"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 12px',
            borderRadius: 999,
            background: 'rgba(178, 132, 255, 0.10)',
            border: '1px solid rgba(178, 132, 255, 0.30)',
            color: 'var(--text)',
            textDecoration: 'none',
            fontSize: 12, fontWeight: 700,
            letterSpacing: '0.05em',
            transition: 'all 0.15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(178, 132, 255, 0.18)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(178, 132, 255, 0.10)';
          }}
        >
          <Trophy size={12} color="#b284ff" />
          <span style={{ color: '#b284ff' }}>LEAGUE</span>
          <span style={{ color: 'var(--text-2)' }}>·</span>
          <span style={{ color: 'var(--text-2)' }}>Switch to Team</span>
        </Link>

        <Link
          to="/leagues"
          style={{ color: 'var(--text-2)', textDecoration: 'none', fontSize: 13 }}
          onMouseEnter={(e) => e.target.style.color = 'var(--ice)'}
          onMouseLeave={(e) => e.target.style.color = 'var(--text-2)'}
        >
          All leagues →
        </Link>
      </div>

      {isDemo && (
        <div role="note" style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 14px', borderRadius: 12,
          background: 'rgba(251, 191, 36, 0.10)', border: '1px solid rgba(251, 191, 36, 0.35)',
          color: 'var(--text)', fontSize: 13, lineHeight: 1.45,
        }}>
          <FlaskConical size={16} color="#fbbf24" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <strong style={{ color: '#fbbf24' }}>Demo league.</strong>{' '}
            Every team, player and score here is fictional. Demo people are never emailed and card payments are simulated.
          </div>
          {user?.role === 'admin' && (
            <Link to="/admin?tab=demo" style={{ color: '#fbbf24', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              Demo controls →
            </Link>
          )}
        </div>
      )}

      {/* v2.5.131 — Tab strip removed.
          The same nav items render in the LEFT SIDEBAR on all 4
          layouts now (DesktopLayout / TabletLayout / MobileLayout /
          CapacitorLayout via useLeagueModeNav, shipped in v2.5.124).
          Rendering the strip here as well duplicated 11 nav targets
          on every league page. The header above (league name + Switch
          to Team pill + All leagues link) is kept because none of
          those affordances appear in the sidebar.

          If for some future reason a page needs to render WITHOUT a
          left-side league nav (e.g. an embed view), the strip can
          be re-introduced gated on a prop like `showTabs={true}`. */}
    </div>
  );
}
